/**
 * ChartInstance Component
 *
 * Manages a single WebSocket connection and PortfolioChart instance.
 * Each instance has its own connection status, events, and chart display.
 */

/*
Data example:
WebSocket message: Portfolio Rebalancing Summary {
  "message": "Portfolio Rebalancing Summary",
  "Decision date": "2020-01-23",
  "regime": "BULL",
  "target_positions": 47,
  "actions": {
    "sold": 2,
    "bought": 3,
    "held": 44,
    "total": 47
  },
  "trades": {
    "sold": [
      {
        "ticker": "BKR",
        "alpha": "0.0%"
      },
      {
        "ticker": "FANG",
        "alpha": "0.0%"
      }
    ],
    "bought": [
      {
        "ticker": "BKR",
        "alpha": "80.5%"
      },
      {
        "ticker": "BIIB",
        "alpha": "51.3%"
      },
      {
        "ticker": "MCHP",
        "alpha": "50.6%"
      }
    ],
    "held": [
      {
        "ticker": "FTNT",
        "alpha": "0.0%"
      },
      {
        "ticker": "SNPS",
        "alpha": "0.0%"
      },
      {
        "ticker": "MDLZ",
        "alpha": "0.0%"
      },
      {
        "ticker": "CHTR",
        "alpha": "0.0%"
      },
      {
        "ticker": "TTWO",
        "alpha": "0.0%"
      },
      {
        "ticker": "TSLA",
        "alpha": "0.0%"
      },
      {
        "ticker": "CDW",
        "alpha": "0.0%"
      },
      {
        "ticker": "META",
        "alpha": "0.0%"
      },
      {
        "ticker": "CCEP",
        "alpha": "0.0%"
      },
      {
        "ticker": "DXCM",
        "alpha": "0.0%"
      },
      {
        "ticker": "MDB",
        "alpha": "0.0%"
      },
      {
        "ticker": "KHC",
        "alpha": "0.0%"
      },
      {
        "ticker": "MAR",
        "alpha": "0.0%"
      },
      {
        "ticker": "KDP",
        "alpha": "0.0%"
      },
      {
        "ticker": "KLAC",
        "alpha": "0.0%"
      },
      {
        "ticker": "DDOG",
        "alpha": "0.0%"
      },
      {
        "ticker": "TTD",
        "alpha": "0.0%"
      },
      {
        "ticker": "PANW",
        "alpha": "0.0%"
      },
      {
        "ticker": "AVGO",
        "alpha": "0.0%"
      },
      {
        "ticker": "NVDA",
        "alpha": "0.0%"
      },
      {
        "ticker": "MRVL",
        "alpha": "0.0%"
      },
      {
        "ticker": "AMD",
        "alpha": "0.0%"
      },
      {
        "ticker": "VRTX",
        "alpha": "0.0%"
      },
      {
        "ticker": "MSTR",
        "alpha": "0.0%"
      },
      {
        "ticker": "ADP",
        "alpha": "0.0%"
      },
      {
        "ticker": "WDAY",
        "alpha": "0.0%"
      },
      {
        "ticker": "MNST",
        "alpha": "0.0%"
      },
      {
        "ticker": "WBD",
        "alpha": "0.0%"
      },
      {
        "ticker": "CRWD",
        "alpha": "0.0%"
      },
      {
        "ticker": "PDD",
        "alpha": "0.0%"
      },
      {
        "ticker": "MU",
        "alpha": "0.0%"
      },
      {
        "ticker": "NFLX",
        "alpha": "0.0%"
      },
      {
        "ticker": "VRSK",
        "alpha": "0.0%"
      },
      {
        "ticker": "AZN",
        "alpha": "0.0%"
      },
      {
        "ticker": "REGN",
        "alpha": "0.0%"
      },
      {
        "ticker": "ON",
        "alpha": "0.0%"
      },
      {
        "ticker": "INTU",
        "alpha": "0.0%"
      },
      {
        "ticker": "LIN",
        "alpha": "0.0%"
      },
      {
        "ticker": "CDNS",
        "alpha": "0.0%"
      },
      {
        "ticker": "NXPI",
        "alpha": "0.0%"
      },
      {
        "ticker": "TEAM",
        "alpha": "0.0%"
      },
      {
        "ticker": "PYPL",
        "alpha": "0.0%"
      },
      {
        "ticker": "ZS",
        "alpha": "0.0%"
      }
    ]
  },
  "performance": {
    "portfolio_value": "104746.33",
    "portfolio_return_pct": "4.75",
    "portfolio_annualized_return_pct": "79.74",
    "benchmark_value": 9216.98,
    "benchmark_return_pct": 5.54,
    "alpha_cumulative_pct": -0.79,
    "alpha_annualized_pct": -13.35,
    "sharpe_ratio": 8.29,
    "volatility_annualized_pct": 9.14,
    "days_elapsed": 15,
    "cash_pct": "1.4",
    "num_positions": 47
  }
}
*/

import { useEffect, useState, useRef, useMemo } from 'react';
import PortfolioChart from './PortfolioChart';
import { MESSAGES, STATUS_MESSAGES } from './constants';
import type { RequestData } from './types';

// WebSocket configuration
// Connect to app-bff's WebSocket proxy (which forwards to FinIntellect)
// In dev: Vite proxy handles /ws -> ws://localhost:3001
// In prod: Same-origin WebSocket (ws:// or wss:// based on current protocol)
function getWebSocketUrl(): string {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Authentication token not found. Please log in.');
  }

  // Use relative URL - Vite proxy handles it in dev, same-origin works in prod
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const host = window.location.host; // Includes port in dev, domain in prod
  return `${protocol}//${host}/ws/alpha-compare?token=${encodeURIComponent(
    token
  )}`;
}

const supportedMessages = Object.values(MESSAGES);

// Trade data structure
interface Trade {
  ticker: string;
  alpha: string; // e.g., "80.5%" or "0.0%"
}

// Actions summary
interface Actions {
  sold: number;
  bought: number;
  held: number;
  total: number;
}

// Trades breakdown
interface Trades {
  sold: Trade[];
  bought: Trade[];
  held: Trade[];
}

// Performance metrics
interface PerformanceMetrics {
  portfolio_value: string | number;
  portfolio_return_pct: string | number;
  portfolio_annualized_return_pct: string | number;
  benchmark_value: number;
  benchmark_return_pct: number;
  alpha_cumulative_pct: number;
  alpha_annualized_pct: number;
  sharpe_ratio: number;
  volatility_annualized_pct: number;
  days_elapsed: number;
  cash_pct: string | number;
  num_positions: number;
}

// Portfolio Rebalancing Summary data
interface PortfolioRebalancingData {
  message: string;
  'Decision date': string; // e.g., "2020-01-23" or "2020-01-02 00:00:00"
  regime: string; // e.g., "BULL", "UNKNOWN"
  target_positions: number;
  actions: Actions;
  trades: Trades;
  performance?: PerformanceMetrics;
}

// Initial Positions data
interface InitialPositionsData {
  message: string;
  positions: string[]; // Array of PositionWeight strings
}

// Alpha Performance Summary data
interface AlphaPerformanceData {
  message: string;
  decision_date: string; // e.g., "2020-09-04 00:00:00"
  alpha_cumulative: number;
  alpha_annualized: number;
  portfolio_return: string | number;
  benchmark_return: number;
  portfolio_value: string | number;
  benchmark_value: number;
}

// Simulation Results data
interface PerformanceSummary {
  initial_value: number;
  final_value: number;
  total_return_pct: number;
  total_alpha_pct: number;
  annualized_return_pct: number;
  annualized_alpha_pct: number;
  days_simulated: number;
  total_trades: number;
  final_positions: number;
  final_cash_pct: number;
}

interface SimulationResultsData {
  message: string;
  start_date: string;
  end_date: string;
  trading_dates: number;
  snapshots: unknown[];
  performance_summary: PerformanceSummary;
  scenario_name?: string;
  scenario_config?: {
    name: string;
    start_date: string;
    end_date: string;
    rebalance_frequency_days: number;
  };
}

// Union type for all possible data structures
type EventDataPayload =
  | PortfolioRebalancingData
  | InitialPositionsData
  | AlphaPerformanceData
  | SimulationResultsData
  | Record<string, unknown>; // Fallback for unknown message types

// Main EventData interface
interface EventData {
  message: string;
  data?: EventDataPayload;
  timestamp?: string;
}

// Type guard function for narrowing
function isSimulationResultsData(
  data: EventDataPayload
): data is SimulationResultsData {
  return (
    typeof data === 'object' &&
    data !== null &&
    'performance_summary' in data &&
    'start_date' in data &&
    'end_date' in data
  );
}

interface ChartInstanceProps {
  chartId: number;
  requestData: RequestData;
  defaultExpanded?: boolean;
  onRemove?: () => void;
}

/**
 * Parse period string to days
 */
const parsePeriodToDays = (period: string): number => {
  const match = period.match(/(\d+)(year|years)/i);
  if (!match) {
    console.warn(`Could not parse period: ${period}, defaulting to 365 days`);
    return 365;
  }
  const years = parseInt(match[1], 10);
  return years * 365;
};

/**
 * Calculate expected number of PORTFOLIO_REBALANCING_SUMMARY events
 */
const calculateExpectedEvents = (
  period: string,
  rebalanceFrequencyDays: number
): number => {
  const periodDays = parsePeriodToDays(period);
  const x = periodDays / rebalanceFrequencyDays;
  return Math.ceil(x) + 1;
};

const ChartInstance: React.FC<ChartInstanceProps> = ({
  chartId,
  requestData,
  defaultExpanded = false,
  onRemove,
}) => {
  const [connectionStatus, setConnectionStatus] = useState<
    'disconnected' | 'connecting' | 'connected' | 'completed' | 'error'
  >('disconnected');
  const [progress, setProgress] = useState<number>(0);
  const [message, setMessage] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState<number>(0);
  const wsRef = useRef<WebSocket | null>(null);
  const isConnectingRef = useRef<boolean>(false);
  const mountedRef = useRef<boolean>(true);
  const connectionStatusRef = useRef<string>('disconnected');
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [relevantEvents, setRelevantEvents] = useState<EventData[]>([]);
  const [isExpanded, setIsExpanded] = useState<boolean>(defaultExpanded);

  // Calculate expected number of PORTFOLIO_REBALANCING_SUMMARY events
  const expectedEvents = useMemo(() => {
    return calculateExpectedEvents(
      requestData.period,
      requestData.rebalance_frequency_days ?? 21
    );
  }, [requestData.period, requestData.rebalance_frequency_days]);

  // Extract total_return_pct from simulation results
  const totalReturnPct = useMemo(() => {
    const simulationEvent = relevantEvents.find(
      (event) =>
        event.message === MESSAGES.SIMULATION_RESULTS &&
        event.data &&
        isSimulationResultsData(event.data)
    );
    if (
      simulationEvent?.data &&
      isSimulationResultsData(simulationEvent.data)
    ) {
      return simulationEvent.data.performance_summary.total_return_pct;
    }
    return null;
  }, [relevantEvents]);

  // Update progress based on PORTFOLIO_REBALANCING_SUMMARY events and SIMULATION_RESULTS
  useEffect(() => {
    const rebalancingEventsCount = relevantEvents.filter(
      (event) => event.message === MESSAGES.PORTFOLIO_REBALANCING_SUMMARY
    ).length;

    const hasSimulationResults = relevantEvents.some(
      (event) => event.message === MESSAGES.SIMULATION_RESULTS
    );

    if (hasSimulationResults) {
      setProgress(100);
      connectionStatusRef.current = 'completed';
      setConnectionStatus('completed');
      setMessage(STATUS_MESSAGES.SIMULATION_COMPLETED);
    } else if (expectedEvents > 0) {
      const calculatedProgress = Math.min(
        (rebalancingEventsCount / expectedEvents) * 100,
        99
      );
      setProgress(Number(calculatedProgress.toFixed(2)));
      setMessage(STATUS_MESSAGES.RECEIVING_REBALANCING_EVENTS);
    }
  }, [relevantEvents, expectedEvents]);

  useEffect(() => {
    if (connectionStatus === 'connected') {
      setElapsedSeconds(0);
      timerRef.current = setInterval(() => {
        setElapsedSeconds((s) => s + 1);
      }, 1000);
    } else if (connectionStatus === 'completed' || connectionStatus === 'error') {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [connectionStatus]);

  const connectWebSocket = () => {
    // Prevent multiple simultaneous connections
    if (
      isConnectingRef.current ||
      (wsRef.current && wsRef.current.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }

    // Close existing connection if it exists and is not already closed
    if (wsRef.current) {
      const currentState = wsRef.current.readyState;
      if (
        currentState === WebSocket.CONNECTING ||
        currentState === WebSocket.OPEN
      ) {
        wsRef.current.close();
      }
    }

    isConnectingRef.current = true;
    connectionStatusRef.current = 'connecting';
    setConnectionStatus('connecting');
    setProgress(0);
    setMessage('');
    setError(null);
    setRelevantEvents([]);

    // Track timeout for showing error (delay to avoid false positives)
    let errorTimeoutId: ReturnType<typeof setTimeout> | null = null;

    // Get WebSocket URL (connects to app-bff proxy, which forwards to FinIntellect)
    let wsUrl: string;
    try {
      wsUrl = getWebSocketUrl();
    } catch (error: any) {
      isConnectingRef.current = false;
      connectionStatusRef.current = 'error';
      setConnectionStatus('error');
      setError(error.message || 'Failed to get WebSocket URL');
      return;
    }

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = function () {
      if (!mountedRef.current) {
        ws.close();
        return;
      }

      // Clear any pending error timeout since connection succeeded
      if (errorTimeoutId) {
        clearTimeout(errorTimeoutId);
        errorTimeoutId = null;
      }

      isConnectingRef.current = false;
      connectionStatusRef.current = 'connected';
      setConnectionStatus('connected');
      setError(null); // Clear any previous errors since connection succeeded

      ws.send(JSON.stringify(requestData));
    };

    ws.onmessage = async function (event) {
      try {
        // Handle Blob messages by reading as text
        let messageText: string;
        if (event.data instanceof Blob) {
          messageText = await event.data.text();
        } else if (event.data instanceof ArrayBuffer) {
          messageText = new TextDecoder().decode(event.data);
        } else {
          messageText = event.data;
        }

        const result = JSON.parse(messageText);
        console.log(
          'WebSocket message:',
          result.message || result.data?.message,
          result.data
        );

        const message =
          result.message || result.data?.message || MESSAGES.NO_MESSAGE;
        if (supportedMessages.includes(message)) {
          setRelevantEvents((prev) => [
            ...prev,
            {
              message,
              data: result.data,
              timestamp: result.timestamp || result.data?.timestamp,
            },
          ]);
        }
      } catch (error) {
        console.error(
          `[Chart ${chartId}] Error parsing WebSocket message:`,
          error
        );
      }
    };

    ws.onclose = function () {
      // Clear any pending error timeout
      if (errorTimeoutId) {
        clearTimeout(errorTimeoutId);
        errorTimeoutId = null;
      }

      isConnectingRef.current = false;
      if (!mountedRef.current) return;

      const currentStatus = connectionStatusRef.current;

      // Only show error if connection closed unexpectedly before successfully connecting
      // Add a small delay to avoid showing error for transient connection issues
      if (currentStatus === 'connecting') {
        // Delay showing error to allow for potential quick reconnection or transient issues
        // This prevents error flash when connection succeeds quickly after a brief failure
        errorTimeoutId = setTimeout(() => {
          if (!mountedRef.current) return;
          // Double-check status hasn't changed (connection might have succeeded in the meantime)
          // Also verify this is still the current WebSocket connection
          if (
            (connectionStatusRef.current === 'connecting' ||
              connectionStatusRef.current === 'error') &&
            wsRef.current === ws &&
            (wsRef.current.readyState === WebSocket.CLOSED ||
              wsRef.current.readyState === WebSocket.CLOSING)
          ) {
            connectionStatusRef.current = 'error';
            setConnectionStatus('error');
            setError(`${STATUS_MESSAGES.CONNECTION_FAILED} on ${wsUrl}`);
          }
        }, 1000); // 1 second delay before showing error (allows time for quick recovery)
      } else if (currentStatus !== 'completed' && currentStatus !== 'error') {
        // Connection closed but was previously connected
        connectionStatusRef.current = 'disconnected';
        setConnectionStatus('disconnected');
      }
    };

    ws.onerror = function (err) {
      // Don't set error state immediately - let onclose determine if it's a real failure
      // The error event can fire even for transient issues that resolve
      if (!mountedRef.current) return;

      // Only log error if we're still connecting (not if connection already succeeded)
      // This prevents error spam for transient connection issues that resolve quickly
      if (connectionStatusRef.current === 'connecting') {
        console.warn(
          `[Chart ${chartId}] WebSocket error during connection (may resolve):`,
          err
        );
      }

      // The onclose handler will determine if this was a real failure
      // Don't set error state here to avoid false positives
    };
  };

  useEffect(() => {
    // Auto-connect when component mounts
    mountedRef.current = true;
    connectWebSocket();

    // Cleanup on unmount
    return () => {
      mountedRef.current = false;
      isConnectingRef.current = false;
      if (wsRef.current) {
        // Only close if connection is established or connecting
        const currentState = wsRef.current.readyState;
        if (
          currentState === WebSocket.CONNECTING ||
          currentState === WebSocket.OPEN
        ) {
          wsRef.current.close();
        }
        wsRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className="border-2 border-white/10 rounded-lg p-5 mb-[30px] bg-white/3 backdrop-blur-[10px]"
    >
      <div
        className="flex justify-between items-center mb-5"
      >
        <div
          className="flex items-center gap-3 flex-1"
        >
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="py-1 px-2 bg-transparent border border-white/20 rounded-md cursor-pointer text-sm flex items-center text-foreground"
            title={isExpanded ? 'Collapse' : 'Expand'}
          >
            {isExpanded ? '▼' : '▶'}
          </button>
          <div
            className="flex flex-wrap gap-3 mt-2 text-xs text-muted-foreground"
          >
            <span>
              <strong>Start Date:</strong> {requestData.start_date}
            </span>
            <span>
              <strong>Period:</strong> {requestData.period}
            </span>
            <span>
              <strong>Investment:</strong> ${requestData.investment.toLocaleString()}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {!isExpanded && (
            <div
              className="flex flex-col items-end gap-1"
            >
              <strong
                className="text-xs text-muted-foreground font-normal"
              >
                Total Return
              </strong>
              {totalReturnPct !== null ? (
                <span
                  className={`text-base font-bold ${totalReturnPct >= 0 ? 'text-positive' : 'text-negative'}`}
                >
                  {totalReturnPct >= 0 ? '+' : ''}
                  {totalReturnPct.toFixed(2)}%
                </span>
              ) : (
                <div
                  className="size-4 rounded-full border-2 border-white/20 border-t-primary animate-spin"
                />
              )}
            </div>
          )}
        </div>
        {onRemove && (
          <button
            onClick={onRemove}
            className="px-4 py-2 bg-destructive text-white border-none rounded-md cursor-pointer ml-4 transition-colors duration-200 hover:bg-destructive/90"
          >
            Remove
          </button>
        )}
      </div>

      {isExpanded && (
        <>
          {(connectionStatus === 'connected' ||
            connectionStatus === 'completed') && (
            <div className="mb-5">
              <div
                className="flex justify-between items-center mb-2.5"
              >
                <div className="text-foreground">
                  <strong>Progress:</strong> {progress}%
                </div>
                {connectionStatus === 'connected' && elapsedSeconds > 0 && (
                  <span className="text-xs text-muted-foreground">
                    {elapsedSeconds}s elapsed · typically ~60s
                  </span>
                )}
                {connectionStatus === 'completed' && (
                  <span className="text-xs text-positive">
                    Completed in {elapsedSeconds}s
                  </span>
                )}
              </div>
              <div
                className="w-full h-5 bg-white/10 rounded-[10px] overflow-hidden"
              >
                <div
                  className={`h-full transition-[width] duration-300 ${connectionStatus === 'completed' ? 'bg-positive' : 'bg-primary'}`}
                  style={{ width: `${progress}%` }}
                />
              </div>
              {message && (
                <div
                  className="mt-2.5 italic text-muted-foreground"
                >
                  {message}
                </div>
              )}
            </div>
          )}

          {error && (
            <div
              className="py-4 px-5 bg-destructive/12 border border-destructive/40 rounded-md mb-5 flex justify-between items-start gap-4"
            >
              <div>
                <div className="text-negative font-semibold mb-1.5">
                  Simulation failed
                </div>
                <div className="text-negative/80 text-[13px]">
                  {error}
                </div>
              </div>
              <button
                onClick={connectWebSocket}
                className="py-1.5 px-3.5 bg-transparent border border-destructive/60 rounded-[5px] text-negative cursor-pointer text-[13px] shrink-0 transition-colors duration-150 hover:bg-destructive/15"
              >
                Retry
              </button>
            </div>
          )}

          <PortfolioChart
            data={relevantEvents}
            simulationPending={
              connectionStatus === 'connecting' ||
              connectionStatus === 'connected'
            }
            progress={progress}
            connectionStatus={connectionStatus}
          />
        </>
      )}
    </div>
  );
};

export default ChartInstance;
